import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { ReactComponent as VeryBadEmoji } from "assets/icons/emoji-very-bad.svg"
import { ReactComponent as BadEmoji } from "assets/icons/emoji-bad.svg"
import { ReactComponent as NeutralEmoji } from "assets/icons/emoji-neutral.svg"
import { ReactComponent as GoodEmoji } from "assets/icons/emoji-good.svg"
import { ReactComponent as VeryGoodEmoji } from "assets/icons/emoji-very-good.svg"
import classNames from "classnames"
import { Tooltip } from "antd"
import { Base } from "./styles"

export type Rating =
  | "very bad"
  | "bad"
  | "neutral"
  | "good"
  | "very good"
  | null

const size = "2em"
export const ratingEmojisIcons = {
  "very bad": <VeryBadEmoji width={size} height={size} />,
  bad: <BadEmoji width={size} height={size} />,
  neutral: <NeutralEmoji width={size} height={size} />,
  good: <GoodEmoji width={size} height={size} />,
  "very good": <VeryGoodEmoji width={size} height={size} />,
}

interface RatingEmojisProps {
  value?: Rating
  onClick?: (rate: Rating) => void
  disabled?: boolean
}

const RatingEmojis: React.FC<RatingEmojisProps> = ({
  value,
  onClick,
  disabled = false,
}) => {
  const { t } = useTranslation("@atoms/rating-emojis")
  const [rate, setRate] = useState<Rating>(value ?? null)

  const handleChange = (state: Rating) => {
    if (disabled) {
      return
    }

    const newState = state !== rate ? state : null
    value === undefined && setRate(newState)
    onClick?.(newState)
  }

  useEffect(() => {
    value !== undefined ? setRate(value) : setRate(null)
  }, [value])

  return (
    <Base>
      <Tooltip overlay={t("emojis.very_bad")} placement="bottom">
        <VeryBadEmoji
          className={classNames("very-bad", { active: rate === "very bad" })}
          onClick={() => {
            handleChange("very bad")
          }}
        />
      </Tooltip>
      <Tooltip overlay={t("emojis.bad")} placement="bottom">
        <BadEmoji
          className={classNames("bad", { active: rate === "bad" })}
          onClick={() => {
            handleChange("bad")
          }}
        />
      </Tooltip>
      <Tooltip overlay={t("emojis.neutral")} placement="bottom">
        <NeutralEmoji
          className={classNames("neutral", { active: rate === "neutral" })}
          onClick={() => {
            handleChange("neutral")
          }}
        />
      </Tooltip>
      <Tooltip overlay={t("emojis.good")} placement="bottom">
        <GoodEmoji
          className={classNames("good", { active: rate === "good" })}
          onClick={() => {
            handleChange("good")
          }}
        />
      </Tooltip>
      <Tooltip overlay={t("emojis.very_good")} placement="bottom">
        <VeryGoodEmoji
          className={classNames("very-good", { active: rate === "very good" })}
          onClick={() => {
            handleChange("very good")
          }}
        />
      </Tooltip>
    </Base>
  )
}

export default RatingEmojis
